import React, {
   CSSProperties,
   forwardRef, PropsWithChildren, ReactElement,
   ReactNode,
   Ref, RefAttributes,
   useCallback, useContext,
   useEffect, useImperativeHandle,
   useRef
} from 'react';
import {
   Cascader,
   CascaderOptionType,
   CascaderRefProps,
   CascaderValueType
} from '../../../controls/cascader';
import { convertSize } from '../../../../utils/StyleUtils';
import { FormContext, generateFormFieldId } from '../../form';

/**
 * {@docCategory CascaderInput}
 */
export interface CascaderInputProps {
   /**
    * The name of the input item.
    */
   name?: string;

   /**
    * The value.
    */
   value?: CascaderValueType;

   /**
    * The placeholder text for the input item.
    */
   placeholder?: string;

   /**
    * Set the input item to disabled.
    * @defaultvalue false
    */
   disabled?: boolean;

   /**
    * Determines if a border should be shown around the component.
    * @defaultvalue true
    */
   border?: boolean;

   /**
    * Determines if the component should have internal padding.
    * @defaultvalue true
    */
   padding?: boolean;

   /**
    * The label text displayed before (on the left side of) the input field.
    */
   addonBefore?: ReactNode;

   /**
    * The label text displayed after (on the right side of) the input field.
    */
   addonAfter?: ReactNode;

   /**
    * Sets the width.
    */
   width?: string | number;

   /**
    * Sets the min width.
    */
   minWidth?: string | number;

   /**
    * Sets the max width.
    */
   maxWidth?: string | number;

   /**
    * Set this to focus on form item on first render
    */
   autoFocus?: boolean;

   /**
    * The data options of the cascader.
    */
   options?: Array<CascaderOptionType>;

   /**
    * The render function to display selected options.
    * @defaultvalue label =\> label.join(/)
    */
   displayRender?: (label: string[], selectedOptions?: CascaderOptionType[]) => ReactNode;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;

   /**
    * Callback when finishing selecting
    */
   onChange?: (value: CascaderValueType, selectedOptions?: CascaderOptionType[]) => void;
}

const createStyle = (width?: string | number, minWidth?: string | number, maxWidth?: string | number, style?: CSSProperties) => {
   return {
      width: convertSize(width, '250px'),
      minWidth: convertSize(minWidth, '60px'),
      maxWidth: convertSize(maxWidth),
      ...style
   };
};

export const CascaderInput = forwardRef(function CascaderInput(props: CascaderInputProps, ref: Ref<CascaderRefProps>) {
   const cascaderRef = useRef<CascaderRefProps | null>(null);
   const valueRef = useRef<CascaderValueType | undefined>(props.value);
   const showBorder = props.border === undefined ? true : props.border;
   const padding = props.padding === undefined ? true : props.padding;
   const classNames = ['uf-cascader-input uf-input'];
   const { onChange, width, minWidth, maxWidth, style: styleProp } = props;

   const style = createStyle(width, minWidth, maxWidth, styleProp);

   if (showBorder) {
      classNames.push('uf-input-bordered');
   }

   if (props.className) {
      classNames.push(props.className);
   }

   if (props.disabled) {
      classNames.push('uf-input-disabled');
   }

   useEffect(()=>{
      valueRef.current=props.value;
   }, [props.value]);

   const onChangeHandler = useCallback((value: CascaderValueType, option?: CascaderOptionType[]) => {
      valueRef.current = value;
      if (onChange) {
         onChange(value, option);
      }
   }, [onChange]);

   useImperativeHandle(ref, () => ({
      focus: () => {
         if (cascaderRef.current) {
            cascaderRef.current.focus();
         }
      },
      blur: () => {
         if (cascaderRef.current) {
            cascaderRef.current.blur();
         }
      },
   } as CascaderRefProps));

   const formContext = useContext(FormContext);
   const inputId = generateFormFieldId(formContext.name, props.name);

   return (
      <span
         className={classNames.join(' ')}
         style={style}
      >
         <span className={'uf-input-wrapper'}>
            {props.addonBefore && <span className={'uf-input-addon'}>
               {props.addonBefore}
            </span>}

            <span className={`uf-input-control-container ${padding ? 'uf-cascader-input-control-container-padding' : ''}`}>

               <Cascader
                  id={inputId}
                  ref={cascaderRef}
                  bordered={false}
                  size={'default'}
                  placeholder={props.placeholder}
                  disabled={props.disabled}
                  className={'uf-input-control'}
                  value={props.value}
                  autoFocus={props.autoFocus}
                  options={props.options}
                  onChange={onChangeHandler}
               />

            </span>

            {props.addonAfter && <span className={'uf-input-addon'}>
               {props.addonAfter}
            </span>}
         </span>
      </span>
   );
}) as (props: CascaderInputProps & RefAttributes<CascaderRefProps>) => ReactElement | null;
